import InfiniteScroll from 'react-infinite-scroll-component';
import { useState } from 'react';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { IssuerPaymentItem } from '@/modules/issuer-dashboard/components/items/IssuerPaymentItem.tsx';
import { usePaymentsListQuery } from '@/modules/issuer-dashboard/queries/payments.tsx';
import { CustomCheckbox, EmptyListState, Spinner } from 'ui';
import { GetPaymentsListV1SrPaymentsGetParams, SortedBy } from 'api';

export type PaymentTabFilters = Omit<
  GetPaymentsListV1SrPaymentsGetParams,
  'page_size' | 'page' | 'sorted_by'
>;

export interface IssuerPaymentTabProps {
  register_id: number;
  filters: PaymentTabFilters;
}

export const IssuerPaymentsTab = ({ register_id, filters }: IssuerPaymentTabProps) => {
  const [isShowActionFirst, setIsShowActionFirst] = useState(false);
  const getRegisterTypeStrings = useRegisterTypeStrings();

  const {
    data: issuerPaymentsList,
    fetchNextPage,
    hasNextPage,
    isLoading: isIssuerPaymentsLoading,
  } = usePaymentsListQuery(
    {
      page: 1,
      page_size: 15,
      sorted_by: isShowActionFirst ? SortedBy.Status : SortedBy.RequestedAtDate,
      ...filters,
    },
    false,
    Number(register_id),
  );

  if (isIssuerPaymentsLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-start">
      <div className="w-full flex items-center justify-between pb-4">
        <CustomCheckbox
          label="Show Action required first"
          checked={isShowActionFirst}
          onChange={e => setIsShowActionFirst(e.target.checked)}
          className="select-none"
        />
        <div></div>
      </div>
      <EmptyListState
        title="No payments yet"
        description={`Payments for ${getRegisterTypeStrings('application', {
          plural: true,
        })} or for ${getRegisterTypeStrings('dividend', {
          plural: true,
        })} will appear here.`}
        isLoading={isIssuerPaymentsLoading}
        isSearchUsed={false}
        itemsCount={issuerPaymentsList?.pages.flatMap(({ data }) => data).length ?? 0}
      />
      <InfiniteScroll
        next={fetchNextPage}
        dataLength={issuerPaymentsList?.pages.length ?? 0}
        hasMore={hasNextPage || false}
        style={{ overflow: 'unset' }}
        loader={
          <div className="flex justify-center items-center h-16 w-full">
            <Spinner size="md" variant="primary" />
          </div>
        }
        scrollableTarget="scrollable"
      >
        <div className="w-full flex flex-col space-y-2">
          {issuerPaymentsList?.pages.map(({ data }) =>
            data.map(payment => (
              <IssuerPaymentItem
                payment={payment}
                key={payment.id}
                navigateToHolderPage={true}
                isDeleted={filters.is_deleted}
                showArchiveButton={true}
              />
            )),
          )}
        </div>
      </InfiniteScroll>
    </div>
  );
};
